import {Field, Input as ClayInput, TextArea} from "@bolteu/clay-react";
import {Controller, type Control, type FieldValues, type ValidateResult} from "react-hook-form";

import {useI18n} from "i18n/i18n";

import {FieldLabel} from "../FieldLabel";

interface OwnProps {
    label?: string;
    subtitle?: string;
    placeholder?: string;
    maxLength?: number;
    multiline?: boolean;
    validationRegex?: string;
    validationFunction?: (value: string) => ValidateResult;
    required?: boolean;
    fieldName: string;
    control: Control<FieldValues> | undefined;
    type?: string;
    defaultValue?: string;
    // Tighter spacing (16px vs the usual 24px) for fields that elaborate on the one
    // directly above them, so they read as a group (e.g. persona + its free-text detail).
    grouped?: boolean;
}

export const Input = ({
    label,
    subtitle,
    placeholder,
    maxLength,
    multiline,
    validationRegex,
    required,
    fieldName,
    control,
    type,
    defaultValue,
    validationFunction,
    grouped,
}: OwnProps) => {
    const i18n = useI18n();

    return (
        <Controller
            rules={{
                required: required && i18n("webform.form_field.required_field"),
                validate: validationFunction,
                pattern: validationRegex
                    ? {
                          value: new RegExp(validationRegex),
                          message: i18n("webform.form_field.error"),
                      }
                    : undefined,
            }}
            defaultValue={defaultValue}
            control={control}
            name={fieldName}
            render={({field: {value, onChange}, fieldState: {error}}) => (
                <Field.Root className={grouped ? "mt-4" : "mt-6"} invalid={!!error} required={required}>
                    {!!label && <FieldLabel label={label} subtitle={subtitle} />}
                    {multiline ? (
                        <TextArea
                            fieldSize="md"
                            rows={4}
                            placeholder={placeholder}
                            maxLength={maxLength}
                            value={value ?? ""}
                            onValueChange={onChange}
                            required={required}
                        />
                    ) : (
                        <ClayInput fieldSize="md" value={value ?? ""} onValueChange={onChange}>
                            <ClayInput.Control
                                type={type}
                                placeholder={placeholder}
                                maxLength={maxLength}
                                required={required}
                            />
                        </ClayInput>
                    )}
                    <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
            )}
        />
    );
};
